import FlowireAPI from '../../../flowireapi/FlowireAPI';

/**
 * Builds the sensitivity analysis payload from graph data
 * @param {Object} graph - JointJS graph object
 * @param {Object} econVars - Economic variables
 * @param {Object} sensitivityConfig - Configuration for sensitivity analysis
 * @returns {Object} - Payload for API
 */
export function buildSensitivityPayload(graph, econVars, sensitivityConfig) {
    const joint_Streams = graph.getLinks();
    const joint_Units = graph.getElements();

    // Build input dictionary (same structure as simul9)
    let input_Dictionary = { 'streams': {}, 'units': {} };

    // Process streams
    for (let i = 0; i < joint_Streams.length; i++) {
        if (joint_Streams[i].prop('stream_Number')) {
            let num = joint_Streams[i].prop('stream_Number');
            input_Dictionary['streams'][num] = { 'massFlow': {}, 'massFrac': {} };

            let specified_Stream_Massflows = joint_Streams[i].attributes['specified_Stream_Properties']['massFlow'];
            let specified_Stream_Massfracs = joint_Streams[i].attributes['specified_Stream_Properties']['massFrac'];
            let stream_Components = Object.keys(specified_Stream_Massflows);

            for (let x = 0; x < stream_Components.length; x++) {
                input_Dictionary['streams'][num]['massFlow'][stream_Components[x]] = specified_Stream_Massflows[stream_Components[x]];
                input_Dictionary['streams'][num]['massFrac'][stream_Components[x]] = specified_Stream_Massfracs[stream_Components[x]];
            }
            input_Dictionary['streams'][num]['totalFlow'] = joint_Streams[i].attributes['specified_Stream_Properties']['totalFlow'];
        }
    }

    // Process units
    for (let i = 0; i < joint_Units.length; i++) {
        if (joint_Units[i].prop('unit_Label')) {
            let label = joint_Units[i].prop('unit_Label');
            input_Dictionary['units'][label] = joint_Units[i].attributes['equipment_Properties'];
            if (joint_Units[i].attributes['components']) {
                const top_Level_Components = joint_Units[i].attributes['components'];
                const top_Level_Components_Object = Object.keys(top_Level_Components);
                input_Dictionary['units'][label]['components'] = top_Level_Components_Object;
            }
        }
    }

    // Build graphUnits with only essential properties
    const graphUnits = joint_Units
        .filter(cell => cell.attributes.isUnit === "yes")
        .map(unit => ({
            unit_Label: unit.prop('unit_Label'),
            unitType: unit.attributes.equipment_Properties?.unitType
        }));

    // Build graphLinks with only essential properties
    const graphLinks = joint_Streams
        .filter(link => link.prop('stream_Number'))
        .map(link => ({
            stream_Number: link.prop('stream_Number'),
            CustomName: link.attributes.CustomName
        }));

    // Build connectedLinks - maps unit_Label to array of connected stream CustomNames
    const connectedLinks = {};
    const unitCells = joint_Units.filter(cell => cell.attributes.isUnit === "yes");
    for (const unitCell of unitCells) {
        const unitLabel = unitCell.prop('unit_Label');
        if (unitLabel) {
            const linksConnected = graph.getConnectedLinks(unitCell);
            connectedLinks[unitLabel] = linksConnected.map(link => link.attributes.CustomName);
        }
    }

    // Build the full payload
    const payload = {
        // Simulation data (same as simul9)
        univerDict: input_Dictionary,
        flowSheetDict: input_Dictionary,

        // Full graph data with cells array
        graphData: graph.toJSON(),

        // Graph data for backend processing
        graphUnits,
        graphLinks,
        connectedLinks,

        // Economic data (pass entire object)
        econData: econVars,

        // Sensitivity configuration
        sensitivity: sensitivityConfig,
    };

    return payload;
}

/**
 * Extracts available parameters from the graph for sensitivity analysis
 * @param {Object} graph - JointJS graph object
 * @param {Object} econVars - Economic variables
 * @returns {Object} - Available parameters grouped by type
 */
export function getAvailableParameters(graph, econVars) {
    const parameters = {
        stream: [],
        unit: [],
        economic: [],
        feedProduct: []
    };

    // Get stream parameters
    // Category: 'process' - requires simul9 (mass balance) + sizing + econ
    const links = graph.getLinks();
    links.forEach(link => {
        const streamNumber = link.prop('stream_Number');
        const streamName = link.attributes.CustomName || `Stream ${streamNumber}`;
        if (streamNumber) {
            // Add totalFlow parameter
            parameters.stream.push({
                type: 'stream',
                category: 'process', // Requires mass balance simulation
                streamNumber: streamNumber,
                streamName: streamName,
                property: 'totalFlow',
                label: `${streamName} - Total Flow`,
                currentValue: link.attributes.specified_Stream_Properties?.totalFlow || 0
            });

            // Add component mass flows
            const massFlows = link.attributes.specified_Stream_Properties?.massFlow || {};
            Object.keys(massFlows).forEach(component => {
                parameters.stream.push({
                    type: 'stream',
                    category: 'process', // Requires mass balance simulation
                    streamNumber: streamNumber,
                    streamName: streamName,
                    property: 'massFlow',
                    component: component,
                    label: `${streamName} - ${component} Mass Flow`,
                    currentValue: massFlows[component] || 0
                });
            });

            // Add component mass fractions
            const massFracs = link.attributes.specified_Stream_Properties?.massFrac || {};
            Object.keys(massFracs).forEach(component => {
                parameters.stream.push({
                    type: 'stream',
                    category: 'process', // Requires mass balance simulation
                    streamNumber: streamNumber,
                    streamName: streamName,
                    property: 'massFrac',
                    component: component,
                    label: `${streamName} - ${component} Mass Fraction`,
                    currentValue: massFracs[component] || 0
                });
            });
        }
    });

    // Get unit parameters
    const cells = graph.getCells();
    const units = cells.filter(cell => cell.attributes.isUnit === "yes");

    units.forEach(unit => {
        const unitLabel = unit.prop('unit_Label');
        const equipmentProps = unit.attributes.equipment_Properties || {};
        // selectedDropdownValue is stored at the unit level, not inside equipment_Properties
        const selectedType = unit.attributes.selectedDropdownValue || equipmentProps.selectedDropdownValue || 'Custom';
        const unitType = equipmentProps.unitType;

        // Get display name from shapename (strip HTML tags)
        let displayName = unitLabel; // fallback
        try {
            const shapeName = unit.attributes?.attrs?.shapename?.innerHTML || '';
            const cleanName = shapeName.replace(/<\/?[^>]+>/g, '').trim();
            if (cleanName && !cleanName.includes('<br>')) {
                displayName = cleanName;
            } else if (unitType) {
                displayName = unitType;
            }
        } catch (e) {
            // Keep fallback
        }

        // Handle Feed and Product units - they have Cost parameter
        // Category: 'economic' - only affects economic calculations, no simulation needed
        if (unitType === 'Feed' || unitType === 'feed' || unitType === 'Product' || unitType === 'product') {
            const equipmentEcon = unit.attributes.equipment_Economics || {};
            if (equipmentEcon.Cost && equipmentEcon.Cost.Value !== undefined) {
                const value = equipmentEcon.Cost.Value;
                parameters.feedProduct.push({
                    type: 'unit',
                    category: 'economic', // Only affects economics
                    unitLabel: unitLabel,
                    displayName: displayName,
                    equipmentType: unitType,
                    property: 'economics.Cost',
                    label: `${displayName} - Cost (${equipmentEcon.Cost.Units || 'USD/tonne'})`,
                    currentValue: parseFloat(value) || 0
                });
            }
            return;
        }

        // Check if this is a reactor with reaction parameters
        // Category: 'process' - requires simul9 (mass balance) + sizing + econ
        const isReactor = unit.prop('reaction') === true;
        if (isReactor) {
            // Add conversion parameter
            const conversion = equipmentProps.conversion;
            if (conversion !== undefined && conversion !== null && conversion !== '') {
                parameters.unit.push({
                    type: 'unit',
                    category: 'process', // Requires mass balance simulation
                    unitLabel: unitLabel,
                    displayName: displayName,
                    equipmentType: 'Reactor',
                    property: 'conversion',
                    label: `${displayName} - Conversion (%)`,
                    currentValue: parseFloat(conversion) || 0
                });
            }

            // Add stoichiometry parameters for each component
            const rxData = equipmentProps.rx_Data || {};
            Object.keys(rxData).forEach(component => {
                const stoich = rxData[component]?.stoich;
                if (stoich !== undefined && stoich !== null && stoich !== '') {
                    parameters.unit.push({
                        type: 'unit',
                        category: 'process', // Requires mass balance simulation
                        unitLabel: unitLabel,
                        displayName: displayName,
                        equipmentType: 'Reactor',
                        property: `rx_Data.${component}.stoich`,
                        component: component,
                        label: `${displayName} - ${component} Stoichiometry`,
                        currentValue: parseFloat(stoich) || 0
                    });
                }
            });
        }

        // Standard equipment sizing parameters
        // Category: 'sizing' - requires equipment sizing API + econ
        if (unitLabel && equipmentProps[selectedType]) {
            const typeProps = equipmentProps[selectedType];
            Object.keys(typeProps).forEach(propName => {
                // Skip internal/metadata properties
                if (propName.includes('Input2Value') || propName === 'parameters' || propName === 'parametersLoaded') {
                    return;
                }

                const value = typeProps[propName];
                // Check if it's a numeric value
                if (value !== null && value !== undefined && value !== '' &&
                    (typeof value === 'number' || !isNaN(parseFloat(value)))) {
                    parameters.unit.push({
                        type: 'unit',
                        category: 'sizing', // Requires equipment sizing calculation
                        unitLabel: unitLabel,
                        displayName: displayName,
                        equipmentType: selectedType,
                        property: propName,
                        label: `${displayName} - ${propName}`,
                        currentValue: parseFloat(value) || 0
                    });
                }
            });
        }

        // Equipment economics (Capex, Opex, Duty) - these are outputs, not inputs for sensitivity
        // Skip these as they are calculated values, not input parameters
    });

    // Get economic parameters
    const economicParams = [
        { key: 'interestRate', label: 'Interest Rate', unit: '', multiplier: 1 },
        { key: 'years', label: 'Project Years', unit: 'years', multiplier: 1 },
        { key: 'elecCost', label: 'Electricity Cost', unit: '$/MWh', multiplier: 1 },
        { key: 'langFactor', label: 'Lang Factor', unit: '', multiplier: 1 },
        { key: 'materialFactor', label: 'Material Factor', unit: '', multiplier: 1 },
        { key: 'locationFactor', label: 'Location Factor', unit: '', multiplier: 1 },
        { key: 'operatorCount', label: 'Operator Count', unit: '', multiplier: 1 },
        { key: 'operatorSalary', label: 'Operator Salary', unit: '$/yr', multiplier: 1 },
        { key: 'maintenanceRate', label: 'Maintenance Rate', unit: '%', multiplier: 100 },
        { key: 'additionalCapex', label: 'Additional CAPEX', unit: '$', multiplier: 1 },
        { key: 'additionalOpex', label: 'Additional OPEX', unit: '$/yr', multiplier: 1 },
    ];

    economicParams.forEach(param => {
        parameters.economic.push({
            type: 'economic',
            category: 'economic', // Only affects economics calculations
            property: param.key,
            label: param.label,
            unit: param.unit,
            multiplier: param.multiplier,
            currentValue: econVars[param.key] || 0
        });
    });

    return parameters;
}

/**
 * Runs sensitivity analysis by calling the backend API
 * @param {Object} graph - JointJS graph object
 * @param {Object} econVars - Economic variables
 * @param {Object} selectedParameter - The parameter to vary
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} steps - Number of steps
 * @param {string} returnType - 'NPV' or 'LCOP'
 * @param {Function} getIdTokenClaims - Auth function
 * @param {boolean} isAuthenticated - Auth status
 * @returns {Promise<Object>} - Sensitivity analysis results
 */
async function sensitivityAnalysis(
    graph,
    econVars,
    selectedParameter,
    min,
    max,
    steps,
    returnType,
    getIdTokenClaims,
    isAuthenticated
) {
    const sensitivityConfig = {
        parameter: selectedParameter,
        min: min,
        max: max,
        steps: steps,
        returnType: returnType,
        // Category determines which API path the backend uses:
        // - 'process': simul9 (mass balance) + sizing + econ
        // - 'sizing': equipment sizing API + econ
        // - 'economic': econ only
        category: selectedParameter.category || 'economic'
    };

    const payload = buildSensitivityPayload(graph, econVars, sensitivityConfig);
    console.log('Sensitivity Analysis Payload:', payload);

    try {
        const response = await FlowireAPI.authPost(
            '/api/sensitivityAnalysis',
            payload,
            getIdTokenClaims,
            isAuthenticated,
            (error) => {
                throw new Error(`HTTP error! status: ${error}`);
            }
        );

        console.log('Sensitivity Analysis Response:', response);
        return response;
    } catch (error) {
        console.error('Sensitivity analysis error:', error);
        throw error;
    }
}

export default sensitivityAnalysis;
